/**
 * 💰 O FORMATADOR DE R$ ÚNICO (item 43 C54 da matriz de aderência: "145 arquivos montam `style: "currency"`").
 *
 * Um número em reais tem UM jeito de virar texto na Regula e no portal do cliente. Antes cada arquivo montava o seu
 * `toLocaleString("pt-BR", { style: "currency", ... })` com casas diferentes (0, 2, "até 2"), e o mesmo valor saía
 * "R$ 1.234" numa tela e "R$ 1.234,50" na outra. A régua `tests/regula-moeda-formatador-unico.test.ts` recusa
 * formatação de moeda montada à mão nos arquivos da Regula e do portal; o resto da casa migra aos poucos (catraca).
 *
 * PURO e sem dependência: roda no Worker (API, PDF, e-mail) e na tela (importado por caminho relativo, como
 * `evidenciasDoAchado`). O cliente da casa (`src/client/lib/format.ts#money`) delega para cá.
 *
 *   · `reais(v)`          → "R$ 1.234,56" (sempre 2 casas: valor de título, boleto, conta, honorário);
 *   · `reaisInteiros(v)`  → "R$ 1.235"    (sem centavos: estimativa, economia anual, faixa de mercado);
 *   · `reaisPorM2(v)`     → "R$ 12,34 /m²" (preço por área; sempre 2 casas e a unidade junto);
 *   · `reaisEmMil(v)`     → "R$ 12k"      (só eixo de gráfico, onde não cabe o valor inteiro);
 *   · `reaisDeCentavos(c)`→ "R$ 1,50"     (o banco guarda centavos inteiros, ex.: custo de certidão);
 *   · `reaisTexto(v)`     → igual a `reais`, com ESPAÇO comum depois do "R$" (o Intl usa espaço inseparável, U+00A0):
 *     para texto que sai da tela (parecer por e-mail, prompt da Duda, texto de certidão), onde o caractere invisível
 *     atrapalha busca, comparação e cópia.
 * Valor nulo, indefinido ou não finito vira R$ 0,00 (o mesmo que `money` da casa sempre fez); quem precisa de "sem
 * valor" decide ANTES de chamar (o número que não existe não pode virar zero calado na tela errada).
 */

const DUAS_CASAS = new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL", minimumFractionDigits: 2, maximumFractionDigits: 2 });
const SEM_CENTAVOS = new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL", minimumFractionDigits: 0, maximumFractionDigits: 0 });

const numero = (v: number | string | null | undefined): number => {
  const n = typeof v === "string" ? Number(v) : v;
  return typeof n === "number" && Number.isFinite(n) ? n : 0;
};

/** "R$ 1.234,56": sempre 2 casas. */
export function reais(v: number | string | null | undefined): string {
  return DUAS_CASAS.format(numero(v));
}

/** "R$ 1.235": arredondado, sem centavos (estimativas e economias anuais). */
export function reaisInteiros(v: number | string | null | undefined): string {
  return SEM_CENTAVOS.format(numero(v));
}

/** "R$ 12,34 /m²": preço por metro quadrado, a unidade sempre junto do número. */
export function reaisPorM2(v: number | string | null | undefined): string {
  return `${reais(v)} /m²`;
}

/** Centavos inteiros (como o banco guarda custo de certidão) para "R$ 1,50". */
export function reaisDeCentavos(centavos: number | string | null | undefined): string {
  return reais(numero(centavos) / 100);
}

/** "R$ 12k": milhares arredondados, só para eixo de gráfico (o valor exato fica no tooltip, com `reais`). */
export function reaisEmMil(v: number | string | null | undefined): string {
  return `R$ ${(numero(v) / 1000).toFixed(0)}k`;
}

/** "R$ 1.234,56" com espaço comum (texto corrido fora da tela: e-mail, prompt, certidão). */
export function reaisTexto(v: number | string | null | undefined): string {
  return reais(v).replace(/\u00a0/g, " ");
}
